<template>
  <div :class="$style.select_embroidery_modal">
    <h3 :class="$style.modal_title">刺繍サービス設定</h3>
    <MoleculesConsumerStepper>
      <AtomsConsumerStepperItem :active="state.step === 1"> 位置指定 </AtomsConsumerStepperItem>
      <AtomsConsumerStepperItem :active="state.step === 2"> フォント選択 </AtomsConsumerStepperItem>
      <AtomsConsumerStepperItem :active="state.step === 3"> カラー・テキスト設定 </AtomsConsumerStepperItem>
      <AtomsConsumerStepperItem :active="state.step === 4"> 確認 </AtomsConsumerStepperItem>
    </MoleculesConsumerStepper>
    <div :class="$style.embroidery_section">
      <template v-if="state.step === 1">
        <!-- step1 刺繍位置指定 -->
        <div v-if="!isConfirm" :class="$style.warning_message">
          <p>
            ※ ラッピングの設定が行われておりません。ラッピング希望の方は
            <AtomsConsumerButton :class="$style.wrapping_here" @click="emit('openGiftModal', false, true)">
              こちら
            </AtomsConsumerButton>
            より設定を行なってください。
          </p>
        </div>
        <div :class="$style.position_title">
          <h4>位置を指定</h4>
          <div :class="$style.description">
            <p>※ 一箇所のみ</p>
            <p>（サイズは固定：縦1.3㎝ / 横最大6㎝）</p>
          </div>
        </div>
        <div :class="$style.position_img">
          <AtomsConsumerImage :src="positionImg" :width="400" />
        </div>
        <div :class="$style.select_area">
          <AtomsConsumerRadioButton
            v-for="embroideryPosition in positions"
            :key="embroideryPosition"
            :value="embroideryPosition"
            name="embroidery_position"
            :checked="positionValue"
            @change="emit('update:positionValue', $event)"
          >
            {{ embroideryPosition }}
          </AtomsConsumerRadioButton>
        </div>
        <div :class="$style.btn_area">
          <AtomsConsumerButton block variant="primary" :disabled="!positionValue" @click="nextStep(1)">
            次へ
          </AtomsConsumerButton>
          <AtomsConsumerButton block variant="secondary" @click="emit('embroideryCanceled', false)">
            刺繍サービスの設定を解除する
          </AtomsConsumerButton>
        </div>
      </template>
      <template v-else-if="state.step === 2">
        <!-- step2 フォント選択 -->
        <h4>フォントを選択</h4>
        <div :class="$style.select_area">
          <AtomsConsumerRadioButton
            v-for="embroideryFont in EMBROIDERY_FONT"
            :key="embroideryFont.value"
            :value="embroideryFont.value"
            name="embroidery_font"
            :checked="fontValue"
            @change="emit('update:fontValue', $event)"
          >
            <div :class="$style.select_embroidery_font">
              <p>{{ embroideryFont.value }}</p>
              <AtomsConsumerImage :class="$style.font_img" :src="embroideryFont.image" />
            </div>
          </AtomsConsumerRadioButton>
        </div>
        <div :class="$style.btn_area">
          <AtomsConsumerButton block variant="primary" :disabled="!fontValue" @click="nextStep(2)">
            次へ
          </AtomsConsumerButton>
          <AtomsConsumerButton block variant="secondary" @click="prevStep(2)">戻る</AtomsConsumerButton>
        </div>
      </template>
      <template v-else-if="state.step === 3">
        <!-- step3 カラー・テキスト設定 -->
        <h4>刺繍カラーを選択</h4>
        <div :class="[$style.select_area, $style.color]">
          <AtomsConsumerRadioButton
            v-for="color in embroideryColor"
            :key="color.value"
            :value="color.value"
            name="embroidery_color"
            :checked="colorValue"
            @change="emit('update:colorValue', $event)"
          >
            <div :class="$style.select_embroidery_color">
              <AtomsConsumerImage :class="$style.color_img" :src="color.image" :width="50" :height="50" />
              <p>{{ color.value }}</p>
            </div>
          </AtomsConsumerRadioButton>
        </div>
        <MoleculesConsumerFormItem>
          <template #title>刺繍テキストを入力</template>
          <template #title-description>
            {{ fontValue.includes("ひらがな") ? `10文字以内（ひらがな数字のみ）` : `10文字以内（英数字のみ）` }}
          </template>
          <AtomsConsumerInput
            v-model="state.textValue"
            :placeholder="fontValue.includes('ひらがな') ? 'やまだはなこ' : 'Hanako'"
            :input-mode="fontValue.includes('ひらがな') ? 'text' : 'url'"
            :max-length="10"
            @input="onTextChange"
          />
        </MoleculesConsumerFormItem>
        <p v-if="errorEmbroideryTextRef" :class="$style.confirm_attention_text">{{ errorEmbroideryTextRef }}</p>
        <p v-else :class="$style.blank" />
        <div :class="$style.btn_area">
          <AtomsConsumerButton
            block
            variant="primary"
            :disabled="!colorValue || !textValue || !!errorEmbroideryTextRef"
            @click="nextStep(3)"
          >
            次へ
          </AtomsConsumerButton>
          <AtomsConsumerButton block variant="secondary" @click="prevStep(3)">戻る</AtomsConsumerButton>
        </div>
      </template>
      <template v-else-if="state.step === 4">
        <!-- step4 確認 -->
        <h4>刺繍サービスの設定内容</h4>
        <div :class="$style.confirm_section">
          <div :class="$style.confirm_item">
            <p>刺繍位置：</p>
            <p>{{ positionValue }}</p>
          </div>
          <div :class="$style.confirm_item">
            <p>フォント：</p>
            <p>{{ fontValue }}</p>
          </div>
          <div :class="$style.confirm_item">
            <p>カラー：</p>
            <p>{{ colorValue }}</p>
          </div>
          <div :class="$style.confirm_item">
            <p>刺繍テキスト：</p>
            <p>{{ textValue }}</p>
          </div>
          <div :class="$style.confirm_item">
            <p>刺繍料金：</p>
            <p :class="$style.price"> <span :class="$style.tax_include">税込</span>&yen;{{ price }} </p>
          </div>
          <div :class="[$style.complete_image, { [$style.white_color]: colorValue === 'ホワイト' }]">
            <p :class="$style.description">※完成イメージ</p>
            <p
              :class="[
                $style.preview,
                {
                  [$style.cursive]: fontValue === '英数字筆記体',
                  [$style.green]: colorValue === 'グリーン',
                  [$style.blue]: colorValue === 'ブルー',
                  [$style.gold]: colorValue === 'ゴールド',
                  [$style.silver]: colorValue === 'シルバー',
                  [$style.pink]: colorValue === 'ピンク',
                  [$style.white]: colorValue === 'ホワイト',
                },
              ]"
            >
              {{ textValue }}
            </p>
          </div>
          <p v-for="note of noteList" :key="note" :class="$style.note">{{ note }}</p>
          <div :class="$style.btn_area">
            <AtomsConsumerButton block variant="primary" @click="emit('confirm')">
              刺繍サービスの設定を完了する
            </AtomsConsumerButton>
            <AtomsConsumerButton block variant="secondary" @click="prevStep(4)"> 戻る </AtomsConsumerButton>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  EMBROIDERY_FONT,
  EMBROIDERY_HIRAGANA_GOTHIC_COLOR,
  EMBROIDERY_ALPHANUMERIC_CURSIVE_COLOR,
  EMBROIDERY_ALPHANUMERIC_GOTHIC_COLOR,
} from "~/const/embroidery"
import { EventHandler } from "~/types/events"
import { EmbroideryColorMapping, EmbroideryColorMappingKeys } from "~/types/embroidery"
import { useToast } from "~/state"

const props = withDefaults(
  defineProps<{
    textValue: string
    fontValue: EmbroideryColorMappingKeys
    colorValue: string
    positionValue: string
    price: number
    isConfirm: boolean
    positions: string[]
    positionImg: string
  }>(),
  {
    textValue: "",
    fontValue: "英数字ゴシック体",
    colorValue: "",
    positionValue: "",
    positions: () => [],
    positionImg: "",
    isConfirm: false,
    price: 550,
  },
)

type Emits = {
  (e: "confirm"): void
  (e: "embroideryCanceled", isReset: boolean): void
  (e: "openGiftModal", isFromEGift: boolean, isFromEmbroidery: boolean): void
  (e: "update:textValue", value: string): void
  (e: "update:fontValue", value: string): void
  (e: "update:colorValue", value: string): void
  (e: "update:positionValue", value: string): void
}

const errorEmbroideryTextRef = ref<string>("")
const toast = useToast()

const state = ref({
  /**
   * - 1: 刺繍位置指定
   * - 2: フォント選択
   * - 3: カラー・テキスト設定
   * - 4: 確認
   */
  step: 1,
  textValue: props.textValue,
})

const emit = defineEmits<Emits>()

const embroideryColorMapping: EmbroideryColorMapping = {
  ひらがなゴシック体: EMBROIDERY_HIRAGANA_GOTHIC_COLOR,
  英数字筆記体: EMBROIDERY_ALPHANUMERIC_CURSIVE_COLOR,
  英数字ゴシック体: EMBROIDERY_ALPHANUMERIC_GOTHIC_COLOR,
}

const noteList = [
  "※刺繍のため実際とは多少イメージが異なります。",
  "※ご注文いただいた刺繍の文字やデザイン（フォント、カラーなど）については、後からの変更や誤字の訂正を承ることができませんので、注文の際は十分にご確認ください。",
]

const embroideryColor = computed(() => embroideryColorMapping[props.fontValue] || EMBROIDERY_ALPHANUMERIC_GOTHIC_COLOR)

const onTextChange: EventHandler = (e) => {
  // 全角数字を半角に変換
  let text: string = e.target.value
  text = text.replace(/[０-９]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
  })

  text = text.replace(/^\s+|\s+$/g, "")

  text = checkStrLength(text, 10)
  const isValidInput = validateInput(text, props.fontValue)
  const getErrorMessage =
    props.fontValue === "ひらがなゴシック体" ? "ひらがな数字のみ入力可能です" : "英数字のみ入力可能です"
  errorEmbroideryTextRef.value = isValidInput ? "" : getErrorMessage
  emit("update:textValue", text)
}

const checkStrLength = (str: string, limit: number): string => {
  if (str.length > limit) {
    str = str.slice(0, limit)
  }
  return str
}

const validateInput = (input: string, allowType: string) => {
  // ひらがなゴシック体では、基本的なひらがな（ゔ、ゝ、ゞを除外）＋数字＋スペース＋伸ばし棒を許可
  // 英数字は英数字＋スペース＋半角ハイフンを許可
  const hiragana = "\u3041-\u3093\u3095-\u3096" // ひらがなの範囲を指定（ゔ、ゝ、ゞを除外）
  const regex = allowType.includes("ひらがな") ? new RegExp(`^([0-9${hiragana} s\u30FC])+$`) : /^[A-Za-z0-9\s-]+$/
  return regex.test(input)
}

const nextStep = (nowStep: 1 | 2 | 3 | 4): void => {
  if (nowStep === 1) {
    // リセット
    emit("embroideryCanceled", true)
    if (!props.positionValue) {
      toast?.showErrorToasted("刺繍位置を選択してください")
      return
    }
  }
  if (nowStep === 2) {
    if (!props.fontValue) {
      toast?.showErrorToasted("フォントを選択してください")
      return
    }
  }
  if (nowStep === 3) {
    if (!props.colorValue) {
      toast?.showErrorToasted("刺繍カラーを選択してください")
      return
    }
    if (!props.textValue) {
      toast?.showErrorToasted("刺繍テキストを入力してください")
      return
    }
    if (props.textValue.length > 10) {
      toast?.showErrorToasted("刺繍テキストは10文字以内で入力してください")
      return
    }
    if (!validateInput(props.textValue, props.fontValue)) {
      toast?.showErrorToasted(
        props.fontValue === "ひらがなゴシック体" ? "ひらがなのみ入力可能です" : "英数字のみ入力可能です",
      )
      return
    }
  }
  state.value.step = nowStep + 1
}

const prevStep = (nowStep: 1 | 2 | 3 | 4): void => {
  if (nowStep === 3) {
    errorEmbroideryTextRef.value = ""
    emit("update:textValue", "")
  }
  state.value.step = nowStep - 1
}
</script>

<style lang="scss" scoped module>
$embroidery-color-green: #5b9b34;
$embroidery-color-green-shadow: mix($embroidery-color-green, black, 75%);
$embroidery-color-blue: #2f34af;
$embroidery-color-blue-shadow: mix($embroidery-color-blue, black, 75%);
$embroidery-color-gold: #dc9a1a;
$embroidery-color-gold-shadow: mix($embroidery-color-gold, black, 75%);
$embroidery-color-silver: #b9b9b9;
$embroidery-color-silver-shadow: mix($embroidery-color-silver, black, 75%);
$embroidery-color-pink: #ff9696;
$embroidery-color-pink-shadow: mix($embroidery-color-pink, black, 75%);
$embroidery-color-white: #ffffff;
$embroidery-color-white-shadow: mix($embroidery-color-white, black, 75%);

@font-face {
  font-family: "Bickham Script Pro";
  src: url("~/assets/fonts/bickham_script_pro_regular.ttf") format("truetype");
  font-weight: normal;
  font-style: normal;
}

.select_embroidery_modal {
  max-width: $max-width-sm;
  width: calc(100vw - 2rem);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 1rem;
  @include md {
    padding: 2rem 4rem;
  }
  .modal_title {
    color: $primary;
    margin-bottom: 1rem;
    > span {
      font-size: $font-size-12;
      font-weight: normal;
    }
  }
  h4 {
    text-align: left;
    color: $primary;
  }
  .embroidery_section {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    .position_title {
      text-align: left;
      margin-top: 1rem;
      margin-bottom: 1rem;
      display: flex;
      gap: 0.5rem;
      > .description {
        font-size: $font-size-12;
      }
    }

    .confirm_attention_text {
      text-align: left;
    }
    .blank {
      height: 24px;
    }

    .warning_message {
      color: $danger;
      text-align: left;
      padding: 0.5rem;
      font-size: $font-size-14;
      line-height: 1.8;

      .wrapping_here {
        display: inline-block;
        color: $danger;
        text-decoration: underline;
      }
    }

    .position_img {
      width: 100%;
      margin-bottom: 1rem;
      text-align: center;
      > img {
        width: 100%;
        max-width: 400px;
      }
    }

    .select_area {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      .select_embroidery_font {
        display: flex;
        flex-direction: column;
      }
      &.color {
        flex-wrap: wrap;
        flex-direction: row;

        > label {
          width: calc(33% - 0.75rem);
          .color_img {
            width: 50px;
            height: 50px;
          }
        }
      }
    }

    .confirm_section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      text-align: left;
      .confirm_item {
        display: flex;
        gap: 0;
        > p {
          font-size: $font-size-14;
          &:first-child {
            width: 7rem;
          }
        }
        .price {
          @include main-font;
        }
      }

      .complete_image {
        width: 100%;
        height: 160px;
        background-color: $black-5;
        border-radius: 0.5rem;
        position: relative;

        &.white_color {
          background-color: $black-10;
        }

        .description {
          position: absolute;
          top: 2%;
          left: 2%;
          font-size: $font-size-10;
        }
        .preview {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: clamp($font-size-24, 5vw, $font-size-40);
          font-weight: bold;
          white-space: nowrap;
          &.cursive {
            font-family: "Bickham Script Pro";
            font-size: $font-size-60;
            font-weight: normal;
          }

          &.green {
            color: $embroidery-color-green;
            text-shadow: 0.5px 0.5px 1px $embroidery-color-green-shadow;
          }

          &.blue {
            color: $embroidery-color-blue;
            text-shadow: 0.5px 0.5px 1px $embroidery-color-blue-shadow;
          }

          &.gold {
            color: $embroidery-color-gold;
            text-shadow: 0.5px 0.5px 1px $embroidery-color-gold-shadow;
          }

          &.silver {
            color: $embroidery-color-silver;
            text-shadow: 0.5px 0.5px 1px $embroidery-color-silver-shadow;
          }

          &.pink {
            color: $embroidery-color-pink;
            text-shadow: 0.5px 0.5px 1px $embroidery-color-pink-shadow;
          }

          &.white {
            color: $embroidery-color-white;
            text-shadow: 0.5px 0.5px 1px $embroidery-color-white-shadow;
          }
        }
      }
      .note {
        font-size: $font-size-10;
      }
    }
    .btn_area {
      position: sticky;
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 0.5rem;
      bottom: -3rem;
      z-index: 10;
      padding-top: 1rem;
      padding-bottom: 1rem;
      background: linear-gradient(#ffffff00, #ffffffaa 20%);
      .description {
        font-size: $font-size-11;
        color: $text-gray;
        .self_wrapping,
        .explanation {
          display: inline-block;
          font-size: $font-size-11;
          color: $text-gray;
          border-bottom: 1px solid $text-gray;
        }
      }
    }
  }
}
</style>
